import path from 'path'
import fs from 'fs-extra'

import { automock as createAutomockPlugin } from './middleware'
import { bundleMockFiles, writeMockBundle } from './mockBundler'
import { buildMockIndex } from './mockFileUtils'
import type { Plugin } from 'vite'
import type { AutomockPluginOptions } from './types'
import type { MockBundle } from './mockBundler'

export type { AutomockPluginOptions, InspectorOptions } from './types'
export { saveMockData, buildMockIndex, parseMockModule, writeMockFile } from './mockFileUtils'
export type { MockBundle, MockBundleData } from './mockBundler'
export { bundleMockFiles, writeMockBundle } from './mockBundler'

// Client interceptor exports
export {
  createMockInterceptor,
  initMockInterceptor,
  initMockInterceptorForPureHttp,
  setMockEnabled,
  isMockEnabled,
  loadMockData,
  registerHttpInstance
} from './client'
export type { MockInterceptorOptions, MockBundleData as ClientMockBundleData } from './client/interceptor'

export interface AutomockWithBundleOptions extends AutomockPluginOptions {
  bundleMockData?: boolean
  bundleOutputPath?: string
}

function resolveProductionMockEnabled(
  productionMock: AutomockPluginOptions['productionMock'],
  mode = process.env.NODE_ENV
): boolean {
  if (productionMock === true) return true
  if (productionMock === 'auto') return mode !== 'production'
  return false
}

export function automock(options: AutomockWithBundleOptions = {}): Plugin {
  const {
    bundleMockData = true,
    bundleOutputPath = 'public/mock-data.json',
    ...pluginOptions
  } = options

  const basePlugin = createAutomockPlugin(pluginOptions)

  let cachedBundle: MockBundle | null = null
  let productionMockEnabled = resolveProductionMockEnabled(pluginOptions.productionMock)

  const resolveBundleOutputPath = () =>
    path.isAbsolute(bundleOutputPath)
      ? bundleOutputPath
      : path.join(process.cwd(), bundleOutputPath)

  const ensureBundleExists = () => {
    const outputPath = resolveBundleOutputPath()
    if (!fs.existsSync(outputPath) && cachedBundle) {
      console.log('[automock] Re-writing mock bundle...')
      writeMockBundle(cachedBundle, outputPath)
    }
  }

  return {
    ...basePlugin,
    name: 'vite-plugin-automock-with-bundle',
    config(userConfig, env) {
      if (typeof basePlugin.config === 'function') {
        basePlugin.config.call(this, userConfig, env)
      }
      // Inject compile-time constant for client interceptor
      productionMockEnabled = resolveProductionMockEnabled(
        pluginOptions.productionMock,
        env.mode
      )
      return {
        define: {
          __AUTOMOCK_ENABLED__: JSON.stringify(productionMockEnabled),
        },
      }
    },
    buildEnd: async () => {
      if (bundleMockData && productionMockEnabled) {
        try {
          const mockDir = pluginOptions.mockDir || path.join(process.cwd(), 'mock')

          if (!fs.existsSync(mockDir)) {
            console.log('[automock] Mock directory not found, skipping bundle generation')
            console.log('[automock] Using existing mock-data.json if present')
            return
          }

          const mockFileMap = await buildMockIndex(mockDir)
          if (mockFileMap.size === 0) {
            console.log('[automock] No mock files found, skipping bundle generation')
            return
          }

          cachedBundle = await bundleMockFiles({ mockDir })

          const outputPath = resolveBundleOutputPath()
          writeMockBundle(cachedBundle, outputPath)
          console.log(`[automock] Mock bundle written to: ${outputPath}`)
        } catch (error) {
          console.error('[automock] Failed to bundle mock data:', error)
        }
      }
    },
    writeBundle: ensureBundleExists,
    closeBundle: ensureBundleExists,
  }
}
